import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { MessageSquare, Send, Loader2, Trash2, User as UserIcon, Pencil, Check, X, Filter, AtSign } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Iter88zbp (2026-02-19) — Bemerkungen (Timeline).
 * Ersetzt das nicht-sichtbare `project.notes`-Feld. Jede Bemerkung wird
 * mit Autor, Rolle und Zeitstempel gespeichert und für ALLE mit Projekt-
 * zugriff sichtbar. Legacy-`project.notes` wird beim ersten Abruf im
 * Backend automatisch als 1. Timeline-Eintrag migriert.
 *
 * Iter88zbr (2026-02-19) — @Vorname-Erwähnungen (Backend erkennt, hier
 * farblich hervorgehoben) + Filter-Chips (Meine · 7 Tage · Erwähnungen).
 */

const ROLE_LABEL = {
  super_admin: "Super-Admin",
  admin: "Administrator",
  sales_director: "Vertriebsdirektor",
  sales_manager: "Vertriebsleiter",
  branch_manager: "Niederlassungsleiter",
  sales_rep: "Vertrieb",
  telesales_director: "Telesales-Direktor",
  agent: "Innendienst",
  installer: "Installation",
};

const formatDate = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("de-DE", {
      day: "2-digit", month: "2-digit", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
};

/**
 * Iter88zbr — Rendert Text mit farblichem @-Highlight.
 * Der Backend-Parser matcht `@Vorname` exakt — hier machen wir das
 * gleiche Regex, um erwähnte User optisch hervorzuheben (grüne Pille).
 * Der eingeloggte User wird zusätzlich dick markiert.
 *
 * `resolvedHandles` = Set der Vornamen (lowercase), die das Backend
 * tatsächlich als existierende User aufgelöst hat. Nur diese Handles
 * werden grün angezeigt — nicht gematchte @Xyz bleiben normaler Text,
 * damit kein falscher „wurde benachrichtigt"-Eindruck entsteht.
 */
// Ohne Lookbehind – iPadOS/Safari < 16.4 kann `(?<!…)` nicht parsen (→ schwarzer Bildschirm).
const MENTION_RE = /(^|[^\w@])@([A-Za-zÄÖÜäöüß][\w-]{1,40})/g;
const renderTextWithMentions = (text, meFirstName, resolvedHandles) => {
  if (!text) return null;
  const nodes = [];
  let last = 0;
  const me = (meFirstName || "").toLowerCase();
  const resolved = resolvedHandles instanceof Set ? resolvedHandles : new Set();
  for (const m of text.matchAll(MENTION_RE)) {
    const start = m.index + m[1].length;
    const raw = m[0].slice(m[1].length);
    const handle = m[2];
    const handleLc = handle.toLowerCase();
    const isResolved = resolved.has(handleLc);
    if (start > last) nodes.push(text.slice(last, start));
    if (!isResolved) {
      // Kein Backend-Match → normal rendern (bleibt lesbar als "@Xyz")
      nodes.push(raw);
    } else {
      const isMe = handleLc === me;
      nodes.push(
        <span
          key={`m-${start}`}
          className={
            "inline-flex items-center gap-0.5 px-1 rounded font-semibold " +
            (isMe
              ? "bg-[#00E575]/25 text-[#00E575] ring-1 ring-[#00E575]/40"
              : "bg-[#00E575]/12 text-[#00E575]")
          }
        >
          <AtSign size={10} className="opacity-70" />{handle}
        </span>
      );
    }
    last = start + raw.length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes;
};

export default function NotesSection({ project }) {
  const { user } = useAuth();
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  // Iter88zbq — Inline-Edit-State: {id, text, saving}
  const [editing, setEditing] = useState(null);
  // Iter88zbr — Filter-Chips: 'all' | 'mine' | 'last7' | 'mentions'
  const [filter, setFilter] = useState("all");
  const textareaRef = useRef(null);
  const canModAny = ["admin", "super_admin", "sales_director"].includes(user?.role);

  const load = async () => {
    if (!project?.id) return;
    setLoading(true);
    try {
      const r = await api.get(`/projects/${project.id}/notes`);
      setNotes(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bemerkungen konnten nicht geladen werden");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [project?.id]);

  const submit = async () => {
    const trimmed = text.trim();
    if (!trimmed) return;
    setSaving(true);
    try {
      const r = await api.post(`/projects/${project.id}/notes`, { text: trimmed });
      setNotes((prev) => [r.data, ...prev]);
      setText("");
      toast.success("Bemerkung gespeichert");
      textareaRef.current?.focus();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (noteId) => {
    if (!window.confirm("Bemerkung wirklich löschen?")) return;
    setDeletingId(noteId);
    try {
      await api.delete(`/projects/${project.id}/notes/${noteId}`);
      setNotes((prev) => prev.filter((n) => n.id !== noteId));
      toast.success("Bemerkung gelöscht");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Löschen fehlgeschlagen");
    } finally {
      setDeletingId(null);
    }
  };

  // Iter88zbq — Inline-Edit: startet mit vorbefülltem Text.
  const startEdit = (n) => setEditing({ id: n.id, text: n.text, saving: false });
  const cancelEdit = () => setEditing(null);
  const saveEdit = async () => {
    if (!editing) return;
    const trimmed = (editing.text || "").trim();
    if (!trimmed) { toast.error("Bemerkung darf nicht leer sein"); return; }
    setEditing((e) => ({ ...e, saving: true }));
    try {
      const r = await api.patch(`/projects/${project.id}/notes/${editing.id}`, { text: trimmed });
      setNotes((prev) => prev.map((n) => (n.id === editing.id ? r.data : n)));
      setEditing(null);
      toast.success("Bemerkung aktualisiert");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Speichern fehlgeschlagen");
      setEditing((prev) => (prev ? { ...prev, saving: false } : null));
    }
  };

  const canModify = (n) => canModAny || n.user_id === user?.id;
  const remaining = 5000 - text.length;

  // Iter88zbr — Client-seitige Filter (Timeline ist typischerweise < 500
  // Einträge → keine Server-Round-Trip nötig).
  const filteredNotes = useMemo(() => {
    if (filter === "all") return notes;
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return notes.filter((n) => {
      if (filter === "mine") return n.user_id === user?.id;
      if (filter === "last7") {
        const ts = n.created_at ? new Date(n.created_at).getTime() : 0;
        return ts && (now - ts) <= sevenDaysMs;
      }
      if (filter === "mentions") {
        return (n.mentioned_user_ids || []).includes(user?.id);
      }
      return true;
    });
  }, [notes, filter, user?.id]);

  const filterCounts = useMemo(() => {
    const now = Date.now();
    const sevenDaysMs = 7 * 24 * 60 * 60 * 1000;
    return {
      all: notes.length,
      mine: notes.filter((n) => n.user_id === user?.id).length,
      last7: notes.filter((n) => n.created_at && (now - new Date(n.created_at).getTime()) <= sevenDaysMs).length,
      mentions: notes.filter((n) => (n.mentioned_user_ids || []).includes(user?.id)).length,
    };
  }, [notes, user?.id]);

  const FILTER_CHIPS = [
    { id: "all", label: "Alle" },
    { id: "mine", label: "Nur meine" },
    { id: "last7", label: "Letzte 7 Tage" },
    { id: "mentions", label: "Erwähnungen für mich" },
  ];

  return (
    <div className="space-y-5">
      {/* Neue Bemerkung erfassen */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5 space-y-3">
        <h2 className="font-display text-lg font-semibold text-white flex items-center gap-2">
          <MessageSquare size={18} className="text-[#00E575]" /> Neue Bemerkung
        </h2>
        <textarea
          ref={textareaRef}
          data-testid="notes-new-input"
          value={text}
          onChange={(e) => setText(e.target.value.slice(0, 5000))}
          onKeyDown={(e) => {
            if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
              e.preventDefault();
              submit();
            }
          }}
          rows={3}
          placeholder="z. B. @Anna bitte Rückruf morgen Nachmittag …  (⌘/Strg-Enter zum Speichern)"
          className="w-full min-h-[88px] bg-[#09090B] border border-zinc-700 rounded-xl px-4 py-3 text-[15px] text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none resize-y"
        />
        <p className="text-[11px] text-zinc-400 -mt-1">
          Tipp: Kollegen mit <span className="text-[#00E575] font-semibold">@Vorname</span> erwähnen — sie bekommen eine Push-Meldung.
        </p>
        <div className="flex items-center justify-between gap-3">
          <span className={`text-[12px] ${remaining < 200 ? "text-amber-400" : "text-zinc-400"}`}>
            {remaining.toLocaleString("de-DE")} Zeichen frei
          </span>
          <button
            type="button"
            data-testid="notes-save-btn"
            onClick={submit}
            disabled={saving || !text.trim()}
            className="h-11 px-5 rounded-xl bg-[#00E575] text-[#09090B] font-bold text-sm inline-flex items-center gap-2 hover:bg-[#00C964] disabled:bg-zinc-800 disabled:text-zinc-500 active:scale-95 transition"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Send size={15} />}
            Speichern
          </button>
        </div>
      </div>

      {/* Timeline */}
      <div className="bg-[#18181B] border border-zinc-800 rounded-2xl p-5">
        <h2 className="font-display text-lg font-semibold text-white flex items-center gap-2 mb-3">
          <MessageSquare size={18} /> Chronik der Bemerkungen
          {!loading && (
            <span className="ml-auto text-[12px] text-zinc-400 font-mono uppercase tracking-wider">
              {filter === "all"
                ? `${notes.length} ${notes.length === 1 ? "Eintrag" : "Einträge"}`
                : `${filteredNotes.length} / ${notes.length}`}
            </span>
          )}
        </h2>
        {/* Iter88zbr — Filter-Chips */}
        {!loading && notes.length > 0 && (
          <div className="flex items-center gap-1.5 flex-wrap mb-4" data-testid="notes-filters">
            <Filter size={13} className="text-zinc-500 mr-0.5" />
            {FILTER_CHIPS.map((c) => {
              const active = filter === c.id;
              const count = filterCounts[c.id] ?? 0;
              return (
                <button
                  key={c.id}
                  type="button"
                  data-testid={`notes-filter-${c.id}`}
                  onClick={() => setFilter(c.id)}
                  className={`h-8 px-3 rounded-full text-[12px] font-semibold inline-flex items-center gap-1.5 transition active:scale-95 ${
                    active
                      ? "bg-[#00E575] text-[#09090B]"
                      : "bg-zinc-800/60 text-zinc-300 hover:bg-zinc-800"
                  }`}
                >
                  {c.label}
                  <span className={`inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full text-[10px] font-mono ${
                    active ? "bg-[#09090B]/20 text-[#09090B]" : "bg-zinc-900 text-zinc-400"
                  }`}>
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        )}
        {loading ? (
          <div className="flex items-center justify-center py-8 text-zinc-500">
            <Loader2 className="animate-spin mr-2" size={16} /> Lade …
          </div>
        ) : filteredNotes.length === 0 ? (
          <div className="text-center py-10 text-zinc-500 text-sm" data-testid="notes-empty-state">
            {notes.length === 0
              ? "Noch keine Bemerkungen. Die erste Bemerkung erscheint hier."
              : `Keine Einträge für Filter „${FILTER_CHIPS.find((c) => c.id === filter)?.label}".`}
          </div>
        ) : (
          <div className="relative pl-7 space-y-4 border-l-2 border-zinc-800" data-testid="notes-timeline">
            {filteredNotes.map((n) => {
              const isEditing = editing?.id === n.id;
              const mentionsMe = (n.mentioned_user_ids || []).includes(user?.id);
              return (
              <div key={n.id} className="relative" data-testid={`notes-item-${n.id}`}>
                <div className={`absolute -left-[34px] top-1 w-6 h-6 rounded-full flex items-center justify-center ${
                  mentionsMe ? "bg-[#00E575] text-[#09090B]" : "bg-[#18181B] border-2 border-[#00E575]"
                }`}>
                  {mentionsMe ? <AtSign size={12} strokeWidth={3} /> : <UserIcon size={11} className="text-[#00E575]" />}
                </div>
                <div className={`bg-[#0E0E12] border rounded-xl p-3.5 ${
                  mentionsMe ? "border-[#00E575]/40 ring-1 ring-[#00E575]/20" : "border-zinc-800"
                }`}>
                  <div className="flex items-start justify-between gap-3 mb-2">
                    <div className="min-w-0">
                      <div className="font-semibold text-zinc-100 text-[14px] truncate">
                        {n.author_name || "Unbekannt"}
                        {mentionsMe && (
                          <span
                            data-testid={`notes-mentions-me-${n.id}`}
                            className="ml-2 text-[10px] font-mono uppercase tracking-wider text-[#00E575] bg-[#00E575]/12 ring-1 ring-[#00E575]/30 px-1.5 py-0.5 rounded"
                          >
                            @ Erwähnt dich
                          </span>
                        )}
                        {n.legacy_seed && (
                          <span className="ml-2 text-[10px] font-mono uppercase tracking-wider text-amber-300/80 bg-amber-500/10 px-1.5 py-0.5 rounded">
                            Anlagebemerkung
                          </span>
                        )}
                        {n.edited_at && (
                          <span
                            className="ml-2 text-[10px] font-mono uppercase tracking-wider text-zinc-400 bg-zinc-800/60 px-1.5 py-0.5 rounded"
                            title={`Bearbeitet am ${formatDate(n.edited_at)}${n.edited_by_name ? ` von ${n.edited_by_name}` : ""}`}
                          >
                            bearbeitet
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-zinc-400 font-mono uppercase tracking-wider">
                        {ROLE_LABEL[n.author_role] || n.author_role || ""}
                        {n.author_role ? " · " : ""}
                        {formatDate(n.created_at)}
                      </div>
                    </div>
                    {!isEditing && canModify(n) && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          data-testid={`notes-edit-${n.id}`}
                          onClick={() => startEdit(n)}
                          title="Bemerkung bearbeiten"
                          className="h-8 w-8 rounded-lg text-zinc-500 hover:text-[#00E575] hover:bg-[#00E575]/10 flex items-center justify-center transition"
                        >
                          <Pencil size={13} />
                        </button>
                        <button
                          type="button"
                          data-testid={`notes-delete-${n.id}`}
                          onClick={() => remove(n.id)}
                          disabled={deletingId === n.id}
                          title="Bemerkung löschen"
                          className="h-8 w-8 rounded-lg text-zinc-500 hover:text-rose-300 hover:bg-rose-500/10 flex items-center justify-center transition disabled:opacity-50"
                        >
                          {deletingId === n.id ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                        </button>
                      </div>
                    )}
                  </div>
                  {isEditing ? (
                    <div className="space-y-2">
                      <textarea
                        data-testid={`notes-edit-input-${n.id}`}
                        value={editing.text}
                        onChange={(e) => setEditing((prev) => ({ ...prev, text: e.target.value.slice(0, 5000) }))}
                        onKeyDown={(e) => {
                          if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                            e.preventDefault();
                            saveEdit();
                          } else if (e.key === "Escape") {
                            e.preventDefault();
                            cancelEdit();
                          }
                        }}
                        autoFocus
                        rows={Math.max(3, (editing.text || "").split("\n").length)}
                        className="w-full bg-[#09090B] border border-zinc-700 rounded-lg px-3 py-2 text-[14px] text-zinc-50 focus:border-[#00E575] focus:ring-1 focus:ring-[#00E575] transition outline-none resize-y"
                      />
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          data-testid={`notes-edit-cancel-${n.id}`}
                          onClick={cancelEdit}
                          disabled={editing.saving}
                          className="h-9 px-3 rounded-lg bg-zinc-800/60 hover:bg-zinc-800 text-zinc-200 text-[13px] font-medium inline-flex items-center gap-1.5 active:scale-95 transition disabled:opacity-50"
                        >
                          <X size={13} /> Abbrechen
                        </button>
                        <button
                          type="button"
                          data-testid={`notes-edit-save-${n.id}`}
                          onClick={saveEdit}
                          disabled={editing.saving || !editing.text?.trim()}
                          className="h-9 px-4 rounded-lg bg-[#00E575] text-[#09090B] font-bold text-[13px] inline-flex items-center gap-1.5 hover:bg-[#00C964] disabled:bg-zinc-800 disabled:text-zinc-500 active:scale-95 transition"
                        >
                          {editing.saving ? <Loader2 size={13} className="animate-spin" /> : <Check size={13} />}
                          Speichern
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="text-[14px] text-zinc-100 whitespace-pre-wrap leading-relaxed" data-testid={`notes-text-${n.id}`}>
                      {renderTextWithMentions(
                        n.text,
                        user?.first_name,
                        new Set((n.mentioned_users || []).map((mu) =>
                          (mu.name || "").split(/\s+/)[0].toLowerCase()
                        ).filter(Boolean)),
                      )}
                    </div>
                  )}
                </div>
              </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
